// services/tournamentService.ts
// Service layer for tournament operations

import {
  ref,
  onValue,
  push,
  set,
  update,
  remove,
  get,
  Unsubscribe,
} from 'firebase/database';
import { db } from '../config/firebase';

// Types
export interface TournamentData {
  name?: string;
  status?: 'active' | 'archived';
  currentRound?: number;
  createdAt?: number;
  updatedAt?: number;
  archivedAt?: number | null;
  timerStart?: number | null;
  timerDuration?: number;
  timerColor?: string;
  [key: string]: unknown;
}

export interface Tournament extends TournamentData {
  id: string;
}

export interface TournamentMetadata {
  name?: string;
  status?: 'active' | 'archived';
  createdAt?: number;
  updatedAt?: number;
  currentRound?: number;
  engineStatus?: string;
  playerCount?: number;
  mode?: string;
  // Navigation-related fields (for permission filtering, ~500 bytes extra)
  isPublic?: boolean;
  staffAssignments?: Record<string, boolean>; // Just IDs, not full staff data
  allowedRoles?: string[];
  format?: string; // engine.settings.format
  floorTeams?: Record<string, { teamLeadId?: string; assistantId?: string }>;
  // Tournament ownership
  ownerUid?: string;
  ownerStoreId?: string;
}

export interface TimerData {
  duration: number;
  start: number | null;
  color: string;
}

type TournamentsCallback = (tournaments: Tournament[]) => void;
type TimerCallback = (timer: TimerData) => void;

/**
 * Sync tournament metadata to the lightweight /tournamentMetadata collection
 * This avoids downloading full tournament data (deckLists, logs, etc.) for list views
 */
async function syncMetadata(id: string, data: Partial<TournamentMetadata>): Promise<void> {
  try {
    await update(ref(db, `tournamentMetadata/${id}`), {
      ...data,
      updatedAt: Date.now(),
    });
  } catch (err) {
    console.error('[tournamentService] Failed to sync metadata:', err);
  }
}

/**
 * Tournament Service - Centralized tournament operations
 */
export const tournamentService = {
  /**
   * Create a new tournament
   */
  async create(data: TournamentData): Promise<string> {
    const tournamentsRef = ref(db, 'tournaments');
    const newRef = push(tournamentsRef);
    const id = newRef.key!;

    const tournamentData: TournamentData = {
      ...data,
      status: 'active',
      currentRound: 1,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };

    await set(newRef, tournamentData);

    // Sync to lightweight metadata collection
    await syncMetadata(id, {
      name: tournamentData.name,
      status: 'active',
      createdAt: tournamentData.createdAt,
      currentRound: 1,
      playerCount: 0,
      mode: data.mode as string,
    });

    return id;
  },

  /**
   * Get a single tournament by ID
   */
  async getById(id: string): Promise<Tournament | null> {
    const snapshot = await get(ref(db, `tournaments/${id}`));
    if (!snapshot.exists()) return null;
    return { id, ...snapshot.val() };
  },

  // NOTE: the old `subscribe(id)` helper (a live onValue on the ENTIRE
  // tournament doc) was deliberately removed. On a large event that node is
  // multi-MB and re-streams in full on every write. Subscribe to specific
  // leaves instead (see TournamentContext for the pattern) or use getById()
  // for one-shot reads.

  /**
   * Subscribe to all active tournaments
   * OPTIMIZED: Uses tournamentMetadata (~1KB) instead of full tournaments (~5MB+)
   */
  subscribeToActive(callback: TournamentsCallback): Unsubscribe {
    return onValue(ref(db, 'tournamentMetadata'), (snapshot) => {
      const data = snapshot.val() || {};
      const tournaments = Object.entries(data)
        .filter(([, t]) => (t as TournamentMetadata).status === 'active')
        .map(([id, t]) => {
          const m = t as TournamentMetadata;
          return {
            id,
            name: m.name,
            status: m.status,
            currentRound: m.currentRound,
            createdAt: m.createdAt,
            updatedAt: m.updatedAt,
          } as Tournament;
        });
      callback(tournaments);
    });
  },

  /**
   * Subscribe to archived tournaments
   * OPTIMIZED: Uses tournamentMetadata (~1KB) instead of full tournaments (~5MB+)
   */
  subscribeToArchived(callback: TournamentsCallback): Unsubscribe {
    return onValue(ref(db, 'tournamentMetadata'), (snapshot) => {
      const data = snapshot.val() || {};
      const tournaments = Object.entries(data)
        .filter(([, t]) => (t as TournamentMetadata).status === 'archived')
        .map(([id, t]) => {
          const m = t as TournamentMetadata;
          return {
            id,
            name: m.name,
            status: m.status,
            currentRound: m.currentRound,
            createdAt: m.createdAt,
            updatedAt: m.updatedAt,
          } as Tournament;
        });
      callback(tournaments);
    });
  },

  /**
   * Update tournament data
   */
  async update(id: string, data: Partial<TournamentData>): Promise<void> {
    await update(ref(db, `tournaments/${id}`), {
      ...data,
      updatedAt: Date.now(),
    });

    // Sync relevant fields to metadata collection
    const metadataFields: Partial<TournamentMetadata> = {};
    if (data.name !== undefined) metadataFields.name = data.name;
    if (data.status !== undefined) metadataFields.status = data.status;
    if (data.currentRound !== undefined) metadataFields.currentRound = data.currentRound;
    if (data.mode !== undefined) metadataFields.mode = data.mode as string;
    if (Object.keys(metadataFields).length > 0) {
      await syncMetadata(id, metadataFields);
    }
  },

  /**
   * Archive a tournament
   */
  async archive(id: string): Promise<void> {
    await this.update(id, { status: 'archived', archivedAt: Date.now() });
    await syncMetadata(id, { status: 'archived' });
  },

  /**
   * Restore an archived tournament
   */
  async restore(id: string): Promise<void> {
    await this.update(id, { status: 'active', archivedAt: null });
    await syncMetadata(id, { status: 'active' });
  },

  /**
   * Delete a tournament permanently
   */
  async delete(id: string): Promise<void> {
    await Promise.all([
      remove(ref(db, `tournaments/${id}`)),
      remove(ref(db, `tournamentMetadata/${id}`)),
      // Wipe the tournament's chat (floor team rooms + reach-a-role request
      // threads all live under tournamentChats/{id}). Best-effort here; a
      // Cloud Function (onTournamentDeleted) guarantees the cleanup with admin
      // privileges once tournaments/{id} is gone.
      remove(ref(db, `tournamentChats/${id}`)).catch(() => undefined),
    ]);
  },

  /**
   * Advance to next round
   */
  async nextRound(id: string): Promise<void> {
    const tournament = await this.getById(id);
    if (!tournament) throw new Error('Tournament not found');

    await this.update(id, {
      currentRound: (tournament.currentRound || 1) + 1,
      timerStart: null,
    });
  },

  /**
   * Go back to previous round
   */
  async previousRound(id: string): Promise<void> {
    const tournament = await this.getById(id);
    if (!tournament) throw new Error('Tournament not found');

    const newRound = Math.max(1, (tournament.currentRound || 1) - 1);
    await this.update(id, {
      currentRound: newRound,
      timerStart: null,
    });
  },

  // Timer operations
  timer: {
    /**
     * Start the timer
     */
    async start(tournamentId: string): Promise<void> {
      await update(ref(db, `tournaments/${tournamentId}`), {
        timerStart: Date.now(),
      });
    },

    /**
     * Pause the timer
     */
    async pause(tournamentId: string, remainingSeconds: number): Promise<void> {
      await update(ref(db, `tournaments/${tournamentId}`), {
        timerStart: null,
        timerDuration: remainingSeconds,
      });
    },

    /**
     * Reset the timer
     */
    async reset(tournamentId: string, duration: number = 50 * 60): Promise<void> {
      await update(ref(db, `tournaments/${tournamentId}`), {
        timerStart: null,
        timerDuration: duration,
      });
    },

    /**
     * Set timer duration
     */
    async setDuration(tournamentId: string, duration: number): Promise<void> {
      await update(ref(db, `tournaments/${tournamentId}`), {
        timerDuration: duration,
      });
    },

    /**
     * Set timer color
     */
    async setColor(tournamentId: string, color: string): Promise<void> {
      await update(ref(db, `tournaments/${tournamentId}`), {
        timerColor: color,
      });
    },

    /**
     * Subscribe to timer state
     * OPTIMIZED: Uses granular subscriptions to timer fields instead of entire tournament
     */
    subscribe(tournamentId: string, callback: TimerCallback): Unsubscribe {
      // Track current state from individual subscriptions
      let currentState: TimerData = {
        duration: 50 * 60,
        start: null,
        color: '#00FFD4',
      };
      let hasInitialData = false;

      const updateCallback = () => {
        if (hasInitialData) {
          callback({ ...currentState });
        }
      };

      // Subscribe to each timer field individually
      const unsubDuration = onValue(
        ref(db, `tournaments/${tournamentId}/timerDuration`),
        (snapshot) => {
          currentState.duration = snapshot.val() || 50 * 60;
          hasInitialData = true;
          updateCallback();
        }
      );

      const unsubStart = onValue(
        ref(db, `tournaments/${tournamentId}/timerStart`),
        (snapshot) => {
          currentState.start = snapshot.val() || null;
          updateCallback();
        }
      );

      const unsubColor = onValue(
        ref(db, `tournaments/${tournamentId}/timerColor`),
        (snapshot) => {
          currentState.color = snapshot.val() || '#00FFD4';
          updateCallback();
        }
      );

      // Return cleanup function that unsubscribes from all
      return () => {
        unsubDuration();
        unsubStart();
        unsubColor();
      };
    },
  },

  /**
   * Get all tournament metadata (lightweight, no deckLists)
   * Use this for tournament list views instead of fetching full tournaments
   */
  async getMetadata(): Promise<Record<string, TournamentMetadata>> {
    const snapshot = await get(ref(db, 'tournamentMetadata'));
    return snapshot.val() || {};
  },

  /**
   * Initialize metadata for existing tournaments
   * Run this once to populate metadata for tournaments created before this feature
   */
  async initializeMetadata(): Promise<number> {
    const tournamentsSnapshot = await get(ref(db, 'tournaments'));
    const tournaments = tournamentsSnapshot.val() || {};
    let count = 0;

    const updates: Record<string, TournamentMetadata> = {};
    for (const [id, tournament] of Object.entries(tournaments)) {
      const t = tournament as TournamentData & {
        engine?: { status?: string; currentRound?: number; players?: Record<string, unknown>; settings?: { format?: string } };
        isPublic?: boolean;
        staffAssignments?: Record<string, unknown>;
        allowedRoles?: string[];
        floorTeams?: Record<string, { teamLeadId?: string; assistantId?: string }>;
      };

      // Convert staffAssignments to just IDs (boolean map)
      const staffIds: Record<string, boolean> = {};
      if (t.staffAssignments) {
        Object.keys(t.staffAssignments).forEach(uid => {
          staffIds[uid] = true;
        });
      }

      updates[`tournamentMetadata/${id}`] = {
        name: t.name,
        status: t.status,
        createdAt: t.createdAt,
        updatedAt: t.updatedAt || Date.now(),
        currentRound: t.engine?.currentRound || t.currentRound || 0,
        engineStatus: t.engine?.status,
        playerCount: t.engine?.players ? Object.keys(t.engine.players).length : 0,
        mode: t.mode as string,
        // Navigation-related fields
        isPublic: t.isPublic,
        staffAssignments: Object.keys(staffIds).length > 0 ? staffIds : undefined,
        allowedRoles: t.allowedRoles,
        format: t.engine?.settings?.format,
        floorTeams: t.floorTeams,
      };
      count++;
    }

    if (Object.keys(updates).length > 0) {
      await update(ref(db), updates);
    }
    return count;
  },

  /**
   * Update metadata from tournament engine (call when round/status changes)
   */
  async syncEngineMetadata(
    tournamentId: string,
    data: { currentRound?: number; engineStatus?: string; playerCount?: number }
  ): Promise<void> {
    await syncMetadata(tournamentId, data);
  },

  /**
   * Sync navigation-related metadata (call when staff/permissions change)
   */
  async syncNavMetadata(
    tournamentId: string,
    data: {
      isPublic?: boolean;
      staffAssignments?: Record<string, boolean>;
      allowedRoles?: string[];
      format?: string;
      floorTeams?: Record<string, { teamLeadId?: string; assistantId?: string }>;
    }
  ): Promise<void> {
    await syncMetadata(tournamentId, data);
  },
};

export default tournamentService;
